import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  SectionHeader,
} from "@babackapp/customer-ui/components/header";
import { OptionsList } from "@babackapp/customer-ui/components/option";
import { RadioGroup } from "@babackapp/customer-ui/components/radio-group";
import {
  ReceiptLine,
  ReceiptLineLabel,
  ReceiptLineValue,
} from "@babackapp/customer-ui/components/receipt-line";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { Alert, AlertDescription } from "@babackapp/ui/components/alert";
import { Separator } from "@babackapp/ui/components/separator";
import { EnterIcon } from "@radix-ui/react-icons";
import { useCheckout } from "../hooks/use-checkout";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useStripeCheckout } from "../hooks/use-stripe-checkout";
import { useWidgetParams } from "../hooks/use-widget-params";
import { ShippingRateOption } from "./shipping-rate-option";
import { StripePaymentElement } from "./stripe-payment-element";

export function Checkout() {
  const { orderId, shopId } = useWidgetParams();
  const {
    financialSummary,
    shippingRates,
    selectedRateId,
    selectRate,
    canProceed,
    onProceed,
    isLoadingRates,
    checkoutToken,
  } = useCheckout();

  const needsPayment = financialSummary.amountDue > 0;

  const stripe = useStripeCheckout(orderId, shopId, checkoutToken);
  const t = useCustomText();

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("checkout.title")}</HeaderTitle>
        <HeaderDescription>
          {needsPayment
            ? t("checkout.descriptionWithPayment")
            : t("checkout.descriptionNoPayment")}
        </HeaderDescription>
      </Header>

      <div className="w-full mt-4 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          {financialSummary.refundTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>{t("financial.refundTotal")}</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(financialSummary.refundTotal, financialSummary.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {financialSummary.exchangeTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>{t("financial.exchangeTotal")}</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(financialSummary.exchangeTotal, financialSummary.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {financialSummary.discountTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>{t("financial.discount")}</ReceiptLineLabel>
              <ReceiptLineValue>
                {formatPrice(financialSummary.discountTotal, financialSummary.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {financialSummary.rebuyTotal > 0 && (
            <ReceiptLine>
              <ReceiptLineLabel>{t("financial.rebuyTotal")}</ReceiptLineLabel>
              <ReceiptLineValue>
                -{formatPrice(financialSummary.rebuyTotal, financialSummary.currency)}
              </ReceiptLineValue>
            </ReceiptLine>
          )}
          {needsPayment && (
            <>
              <Separator className="my-1" />
              <ReceiptLine>
                <ReceiptLineLabel className="font-medium text-foreground">
                  {t("financial.amountDue")}
                </ReceiptLineLabel>
                <ReceiptLineValue className="font-medium text-foreground">
                  {formatPrice(financialSummary.amountDue, financialSummary.currency)}
                </ReceiptLineValue>
              </ReceiptLine>
            </>
          )}
        </div>

        {needsPayment && (
          <>
            {shippingRates.length > 0 && (
              <div className="flex flex-col gap-3">
                <SectionHeader>{t("checkout.shippingRateTitle")}</SectionHeader>
                {isLoadingRates && (
                  <p className="text-sm text-muted-foreground">
                    {t("checkout.loadingRates")}
                  </p>
                )}
                {!isLoadingRates && (
                  <OptionsList>
                    <RadioGroup value={selectedRateId ?? ""} onValueChange={selectRate}>
                      {shippingRates.map((rate) => (
                        <ShippingRateOption
                          key={rate.handle}
                          rate={rate}
                          currency={financialSummary.currency}
                        />
                      ))}
                    </RadioGroup>
                  </OptionsList>
                )}
              </div>
            )}

            <StripePaymentElement
              stripePromise={stripe.stripePromise}
              options={stripe.elementsOptions}
              onReady={stripe.onReady}
              onLoadError={stripe.onLoadError}
            />

            {stripe.errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>{stripe.errorMessage}</AlertDescription>
              </Alert>
            )}
          </>
        )}
      </div>

      <AnimatedButtonRow variants={{}}>
        {needsPayment ? (
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Button
              disabled={!canProceed || !stripe.isReady || stripe.isSubmitting}
              onClick={stripe.onSubmit}
            >
              <EnterIcon />
              {stripe.isSubmitting
                ? t("checkout.paymentPending")
                : t("checkout.payButton", { amount: formatPrice(financialSummary.amountDue, financialSummary.currency) })}
            </Button>
          </AnimatedButtonContainer>
        ) : (
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Button disabled={!canProceed} onClick={onProceed}>
              <EnterIcon />
              {t("common.confirm")}
            </Button>
          </AnimatedButtonContainer>
        )}
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
